import React, { useEffect, useState } from "react";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { useAcenteAuth } from "@/context/AcenteAuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Receipt, CheckCircle2, XCircle, Clock, FileText, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_TINT = {
  beklemede: "bg-amber-100 text-amber-800",
  gonderildi: "bg-emerald-100 text-emerald-800",
  reddedildi: "bg-rose-100 text-rose-800",
};
const STATUS_LABEL = {
  beklemede: "Beklemede",
  gonderildi: "Gönderildi",
  reddedildi: "Reddedildi",
};

export default function AcenteInvoiceRequests() {
  const { user } = useAcenteAuth();
  const role = user?.role;
  const staff = ["admin", "operasyon"].includes(role);

  const [rows, setRows] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [type, setType] = useState("group"); // "group" | "total"
  const [form, setForm] = useState({ reservation_id: "", date_from: "", date_to: "", note: "" });
  const [resolveModal, setResolveModal] = useState(null);
  const [resolveNote, setResolveNote] = useState("");

  const load = () => {
    panelApi.get("/invoice-requests").then((r) => setRows(r.data || [])).catch(() => setRows([]));
    if (role === "acente") {
      panelApi.get("/reservations").then((r) => setReservations(r.data || [])).catch(() => {});
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { request_type: type, note: form.note };
      if (type === "group") payload.reservation_id = form.reservation_id;
      else { payload.date_from = form.date_from; payload.date_to = form.date_to; }
      await panelApi.post("/invoice-requests", payload);
      toast.success("Faturanız 1 iş günü içerisinde tarafınıza iletilecektir.", { duration: 6000 });
      setModalOpen(false);
      setForm({ reservation_id: "", date_from: "", date_to: "", note: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Talep gönderilemedi");
    }
  };

  const resolve = async (action) => {
    if (!resolveModal) return;
    try {
      await panelApi.patch(`/invoice-requests/${resolveModal.id}/resolve`, { action, note: resolveNote });
      toast.success("Talep sonuçlandırıldı");
      setResolveModal(null); setResolveNote("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Sonuçlandırılamadı"); }
  };

  return (
    <div className="space-y-6" data-testid="acente-invoice-requests">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-brand-orange font-semibold mb-2">Fatura Talepleri</div>
          <h1 className="font-serif text-3xl text-brand-navy">Fatura Talepleri</h1>
          <p className="text-sm text-slate-600 mt-1">
            {role === "acente"
              ? "Grup bazında veya toplam olarak fatura talebi oluşturun. Talebiniz 1 iş günü içerisinde tarafınıza iletilir."
              : "Acentelerden gelen fatura taleplerini burada görüp sonuçlandırabilirsiniz."}
          </p>
        </div>
        {role === "acente" && (
          <div className="flex gap-2">
            <Button
              onClick={() => { setType("group"); setModalOpen(true); }}
              className="bg-brand-orange text-white hover:bg-brand-orange-dark"
              data-testid="invoice-request-group-btn"
            >
              <Receipt size={14} className="mr-2" /> Grup Faturası Talep Et
            </Button>
            <Button
              onClick={() => { setType("total"); setModalOpen(true); }}
              variant="outline"
              className="border-brand-navy/20"
              data-testid="invoice-request-total-btn"
            >
              <FileText size={14} className="mr-2" /> Toplam Fatura Talep Et
            </Button>
          </div>
        )}
      </header>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-brand-navy/10 p-12 text-center text-slate-500">
          Henüz fatura talebi yok.
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-brand-navy/10 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 bg-canvas-stone">
              <tr className="text-left">
                <th className="py-3 px-4">Tarih</th>
                {staff && <th className="py-3 px-4">Acente</th>}
                <th className="py-3 px-4">Tür</th>
                <th className="py-3 px-4">Kapsam</th>
                <th className="py-3 px-4">Not</th>
                <th className="py-3 px-4">Durum</th>
                <th className="py-3 px-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-brand-orange/5" data-testid={`invoice-req-row-${r.id}`}>
                  <td className="py-2.5 px-4 font-mono text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  {staff && <td className="py-2.5 px-4 text-xs">{r.agency_name || "-"}<div className="text-[10px] text-slate-400">{r.requester_email}</div></td>}
                  <td className="py-2.5 px-4">
                    <span className="text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 bg-brand-gold/20 text-brand-navy">
                      {r.request_type === "group" ? "Grup" : "Toplam"}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-xs">
                    {r.request_type === "group" ? (
                      <span><span className="font-mono">{r.reservation_number}</span> · {r.group_name}</span>
                    ) : (
                      <span className="font-mono">{formatTr(r.date_from)} → {formatTr(r.date_to)}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-xs text-slate-500 max-w-[240px] truncate" title={r.note}>{r.note || "—"}</td>
                  <td className="py-2.5 px-4">
                    <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 ${STATUS_TINT[r.status]}`}>
                      {STATUS_LABEL[r.status]}
                    </span>
                    {r.status !== "beklemede" && r.resolver_note && (
                      <div className="text-[10px] text-slate-500 mt-1">Not: {r.resolver_note}</div>
                    )}
                  </td>
                  <td className="py-2.5 px-4 text-right">
                    {staff && r.status === "beklemede" && (
                      <button
                        onClick={() => { setResolveModal(r); setResolveNote(""); }}
                        className="text-xs rounded-full px-3 py-1 bg-brand-navy text-white hover:bg-brand-navy/90"
                        data-testid={`invoice-resolve-${r.id}`}
                      >
                        Sonuçlandır
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New request modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="invoice-request-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-brand-navy">
              {type === "group" ? "Grup Faturası Talep Et" : "Toplam Fatura Talep Et"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            {type === "group" ? (
              <div>
                <Label>Rezervasyon *</Label>
                <select
                  required
                  value={form.reservation_id}
                  onChange={(e) => setForm({ ...form, reservation_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  data-testid="invoice-form-reservation"
                >
                  <option value="">— Rezervasyon seçin —</option>
                  {reservations.filter((r) => r.status !== "iptal").map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.reservation_number} — {r.group_name} ({formatTr(r.checkin)})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Başlangıç *</Label>
                  <Input type="date" required value={form.date_from} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="invoice-form-date-from" />
                </div>
                <div>
                  <Label>Bitiş *</Label>
                  <Input type="date" required value={form.date_to} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="invoice-form-date-to" />
                </div>
              </div>
            )}
            <div>
              <Label>Not (isteğe bağlı)</Label>
              <textarea
                rows={3}
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                placeholder="Özel talep, adres, vergi bilgisi vb."
                data-testid="invoice-form-note"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Vazgeç</Button>
              <Button type="submit" className="bg-brand-orange text-white hover:bg-brand-orange-dark" data-testid="invoice-form-submit">
                <Send size={14} className="mr-2" /> Talebi Gönder
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Resolve modal (staff) */}
      <Dialog open={!!resolveModal} onOpenChange={(o) => !o && setResolveModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="invoice-resolve-modal">
          <DialogHeader>
            <DialogTitle className="font-serif text-brand-navy">Fatura Talebini Sonuçlandır</DialogTitle>
          </DialogHeader>
          {resolveModal && (
            <div className="space-y-3">
              <div className="rounded-lg bg-canvas-cream border border-brand-navy/10 p-3 text-xs space-y-1">
                <div><span className="text-slate-500">Acente:</span> {resolveModal.agency_name}</div>
                <div><span className="text-slate-500">Tür:</span> {resolveModal.request_type === "group" ? "Grup" : "Toplam"}</div>
                {resolveModal.reservation_number && <div><span className="text-slate-500">Rez:</span> {resolveModal.reservation_number} — {resolveModal.group_name}</div>}
                {resolveModal.date_from && <div><span className="text-slate-500">Aralık:</span> {resolveModal.date_from} → {resolveModal.date_to}</div>}
                {resolveModal.note && <div><span className="text-slate-500">Not:</span> {resolveModal.note}</div>}
              </div>
              <div>
                <Label>İşlem Notu</Label>
                <textarea
                  rows={3}
                  value={resolveNote}
                  onChange={(e) => setResolveNote(e.target.value)}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  placeholder="Fatura no, gönderim yolu vs."
                  data-testid="invoice-resolve-note"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => resolve("reddedildi")} className="text-rose-700 border-rose-200" data-testid="invoice-resolve-reject">
                  <XCircle size={14} className="mr-2" /> Reddet
                </Button>
                <Button onClick={() => resolve("gonderildi")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invoice-resolve-approve">
                  <CheckCircle2 size={14} className="mr-2" /> Fatura Gönderildi
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
